import { useStyles } from "./Videos.style";
import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import VideoCard from "../../components/common/VideoCard/VideoCard";
import { useQuery, useMutation } from "@apollo/client";
import {
  contentVideosGQL,
  VideoItem,
  VideoType,
  startContentViewGQL,
  completeContentViewGQL,
  UserContentType,
  newsGQL,
} from "@onmo/graphql/graphql";
import LaunchPad from "../../components/common/ServiceSelection/LaunchPad";
import { Typography } from "../../components/core/Typography/Typography";
import { TypographyColor, TypographyVariant } from "../../constants/typography";
import { Icon } from "@onmo/ui";
import clsx from "classnames";
import { debounce, map } from "lodash-es";
import { NewsCard } from "../../components/common/NewsCard";

const listChannel = [
  { name: "Tech Hut", id: "UCjSEJkpGbcZhvo0lr-44X_w" },
  { name: "TheRadBrad", id: "UCsTcErHg8oDvUnTzoqsYeNw" },
  { name: "Unbox Therapy", id: "UCpqXJOEqGS-TCnazcHCo0rA" },
  { name: "KreekCraft", id: "UCxsk7hqE_CwZWGEJEkGanbA" },
  { name: "MKIceAndFire", id: "UC1bwliGvJogr7cWK0nT2Eag" },
  { name: "PlayStation", id: "UC-2Y8dQb0S6DtpxNgAKoJKA" },
  { name: "Xbox", id: "UCjBp_7RuDBUYbd1LegWEJ8g" },
  { name: "Nintendo", id: "UCGIY_O-8vW4rfX98KlMkvRg" },
  { name: "IGN", id: "UCKy1dAqELo0zrOtPkf0eTMw" },
  { name: "Capcom Europe", id: "UCV9Pvsup4LWUWqhKyHWdnkA" },
  { name: "SNK Official", id: "UCI-Q7uYhRVMxyrmcZRf0Egg" },
  { name: "Kotaku", id: "UCVS9tA3PI8Gard_LrTx0YsQ" },
  { name: "SEGA Europe", id: "UCmB-U0lIC2TciZ1nevP9k5g" },
  { name: "Blacknut Cloud Gaming", id: "UC8YGoGazM45cK4jBsDb4lZg" },
  { name: "Fortnite", id: "UClG8odDC8TS6Zpqk9CGVQiQ" },
  { name: "Bandai Namco", id: "UCETrNUjuH4EoRdZNFx9EI-A" },
];

const tabList = [
  { title: "Videos", icon: "icon-play-circle" },
  { title: "News", icon: "icon-bold-global" },
];

const Videos: React.FC = () => {
  const classes = useStyles();
  const [activeVideoIndex, setActiveVideoIndex] = useState<number | null>(0);
  const [activeTab, setActiveTab] = useState(tabList[0]);
  const { i18n } = useTranslation();
  const { data: videosData } = useQuery(contentVideosGQL, {
    variables: {
      type: VideoType.Youtube,
      params: {
        maxResults: 50,
        channelIds: listChannel.map((channel) => channel.id),
      },
    },
  });
  const { data: newsData } = useQuery(newsGQL, {
    variables: {
      params: {
        languages: i18n.language,
      },
    },
  });
  const [startContentView] = useMutation(startContentViewGQL);
  const [completeContentView] = useMutation(completeContentViewGQL);
  const contentViewIdRef = useRef<string | null>(null);

  const onPlayVideo = async (tab?: Record<string, string>) => {
    const contentView = await startContentView({
      variables: {
        input: {
          contentType:
            (tab || activeTab)?.title === "Videos"
              ? UserContentType.Video
              : UserContentType.Article,
        },
      },
    });
    contentViewIdRef.current = contentView?.data?.startContentView?.contentId;
  };

  useEffect(() => {
    const debouncedCheckVideoInCenter = debounce(() => {
      const scrollTop = document.scrollingElement?.scrollTop || 0;
      const currentVideoIndex = Math.floor(
        (scrollTop + window.innerHeight / 4) / 280,
      );
      setActiveVideoIndex(scrollTop < 50 ? 0 : currentVideoIndex);
    }, 500);

    const listenerScrollVideo = () => {
      // deactive video if it's not in center
      setActiveVideoIndex((pre) => {
        if (pre === null || !document.scrollingElement?.scrollTop) return null;
        const currentVideoScrollTop =
          document.scrollingElement?.scrollTop - pre * 280;
        if (
          currentVideoScrollTop < -window.innerHeight / 2 ||
          currentVideoScrollTop > window.innerHeight / 3
        )
          return null;
        return pre;
      });
      debouncedCheckVideoInCenter();
    };

    document.addEventListener("scroll", listenerScrollVideo);
    return () => {
      document.removeEventListener("scroll", listenerScrollVideo);
    };
  }, []);

  useEffect(() => {
    onPlayVideo();
    return () => {
      if (contentViewIdRef.current) {
        completeContentView({
          variables: {
            input: {
              contentId: contentViewIdRef.current,
            },
          },
        });
      }
    };
  }, [completeContentView, startContentView]);

  return (
    <div className={classes.root}>
      <LaunchPad />
      <div className={classes.tabs}>
        {tabList.map((tab) => {
          const active = activeTab.title === tab.title;
          return (
            <div
              className={clsx(classes.tabItem, { active })}
              key={tab.title}
              onClick={() => {
                setActiveTab(tab);
                onPlayVideo(tab);
              }}
            >
              <Icon
                name={tab.icon}
                className={clsx(classes.icon, { active })}
              />
              <Typography
                variant={
                  active
                    ? TypographyVariant.BodyB5Bold
                    : TypographyVariant.BodyB5Regular
                }
                color={
                  active ? TypographyColor.Default : TypographyColor.Silver
                }
              >
                {tab.title}
              </Typography>
            </div>
          );
        })}
      </div>
      <div className={classes.videoList}>
        {activeTab?.title === tabList[0]?.title &&
          (videosData?.contentVideos || [])?.map(
            (video: VideoItem, index: number) => (
              <VideoCard
                video={video}
                isPlayingVideo={index === activeVideoIndex}
                key={video?.id}
              />
            ),
          )}
        {activeTab?.title === tabList[1]?.title &&
          map(newsData?.news?.items, (news, idx) => (
            <NewsCard news={news} key={idx} />
          ))}
      </div>
    </div>
  );
};

export default Videos;
